/**
 * State + row models for the board toolbar row (SE-6488).
 *
 * The toolbar is row 2 of the `TopPanel` header. Its behaviour lives in this
 * hook rather than in a nested toolbar component on purpose: a new component
 * directory is a new renderable surface, which the `/playground` coverage gate
 * requires to ship either a gallery with verified light/dark design provenance
 * or a new audited exception — and the design-system budget ratchet (correctly)
 * refuses new exception debt. A hook carries the same logic with the same
 * testability and adds no surface, so the header stays conformant.
 * @module features/player-kanban/hooks/useBoardToolbar
 */

import { ArrowLeftRight, Hash, Share2 } from "lucide-react-native";
import { useCallback, useMemo, useState } from "react";

import type { DesktopDropdownMenuItem } from "@/components/molecules/DesktopDropdownMenu";
import type { SelectDropdownOption } from "@/components/molecules/SelectDropdown";
import { TEST_ID_BOARD_TOOLBAR } from "e2e/testId";

import {
  BOARD_SORT_DIRECTIONS,
  BOARD_SORT_OPTIONS,
  boardSortButtonLabel,
  type BoardSortDirection,
  type BoardSortOption,
  type BoardSortState,
} from "../constants/boardSort";

import { useStageEditorPanelState } from "./useStageEditorPanelState";

/** Inputs describing what the viewer may reach. */
export interface UseBoardToolbarArgs {
  /** Current board-level sort selection. */
  readonly sort: BoardSortState;
  /** Fired with the newly selected sort field. */
  readonly onSortChange: (option: BoardSortOption) => void;
  /** Fired with the newly selected sort direction. */
  readonly onSortDirectionChange?: (direction: BoardSortDirection) => void;
  /** The open pipeline. */
  readonly boardId?: string;
  /** Whether the board is a roster board. */
  readonly isRosterBoard?: boolean;
  /** Whether the viewer owns the board. */
  readonly isOwner?: boolean;
  /** Whether the viewer may edit the board. */
  readonly canEdit?: boolean;
  /** Whether the viewer may share the board. */
  readonly canShare?: boolean;
  /** Opens the share sheet. */
  readonly onSharePress?: () => void;
  /** Opens the tag manager. */
  readonly onManageTags?: () => void;
}

/** Everything the toolbar markup needs. */
export interface UseBoardToolbarResult {
  readonly sortLabel: string;
  /**
   * Whether a field is sorted — drives the trigger's active look.
   *
   * The trigger recolours to the accent and grows a direction arrow only when
   * this is true; `Default order` reads as the neutral idle state.
   */
  readonly isSortActive: boolean;
  /** Current direction, for the trigger's arrow glyph. */
  readonly sortDirection: BoardSortDirection;
  readonly sortOptions: readonly SelectDropdownOption[];
  readonly isSortOpen: boolean;
  readonly onSortToggle: () => void;
  readonly onSortClose: () => void;
  /** Stages / Share / Tags — preserved, gated exactly as their buttons were. */
  readonly overflowItems: readonly DesktopDropdownMenuItem[];
  readonly isOverflowOpen: boolean;
  readonly onOverflowToggle: () => void;
  readonly onOverflowClose: () => void;
  /**
   * Whether the Activity button should render. Always true.
   *
   * Product decision by Raniel, 2026-08-06 (SE-6554): the pipeline activity &
   * audit log is visible to ALL board viewers, not just owners and editors.
   * This deliberately reverses SE-5504's `isOwner || canEdit` gate.
   *
   * SE-5504's affordance-hiding convention still stands for EDIT actions —
   * Stages, Share, Tags and friends stay gated in `overflowItems` above.
   * Activity is a READ surface, so hiding it bought nothing.
   */
  readonly canSeeActivity: boolean;
}

/**
 * Drive the board toolbar row.
 * @param args - See {@link UseBoardToolbarArgs}.
 * @param args.sort - Current board-level sort selection.
 * @param args.onSortChange - Fired with the newly selected sort field.
 * @param args.onSortDirectionChange - Fired with the newly selected sort direction.
 * @param args.boardId - The open pipeline.
 * @param args.isRosterBoard - Whether the board is a roster board.
 * @param args.isOwner - Whether the viewer owns the board.
 * @param args.canEdit - Whether the viewer may edit the board.
 * @param args.canShare - Whether the viewer may share the board.
 * @param args.onSharePress - Opens the share sheet.
 * @param args.onManageTags - Opens the tag manager.
 * @returns The {@link UseBoardToolbarResult}.
 */
export function useBoardToolbar({
  sort,
  onSortChange,
  onSortDirectionChange,
  boardId,
  isRosterBoard,
  isOwner = false,
  canEdit = false,
  canShare = false,
  onSharePress,
  onManageTags,
}: UseBoardToolbarArgs): UseBoardToolbarResult {
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);

  const onSortToggle = useCallback(() => setIsSortOpen(prev => !prev), []);
  const onSortClose = useCallback(() => setIsSortOpen(false), []);
  const onOverflowToggle = useCallback(
    () => setIsOverflowOpen(prev => !prev),
    []
  );
  const onOverflowClose = useCallback(() => setIsOverflowOpen(false), []);

  const stageEditor = useStageEditorPanelState();

  /*
   * Fields, then a hairline, then the direction — the structure the shared
   * search-modal sort dropdown uses (SE-6554).
   *
   * While the sort is `none` the direction rows render DISABLED rather than
   * disappearing. `none` is the server's own order, so direction genuinely has
   * nothing to act on — but a control that vanishes teaches nothing, whereas a
   * dimmed one says "direction exists, pick a field first". That is what
   * `SortDropdownView` does with the same state.
   */
  const sortOptions = useMemo<SelectDropdownOption[]>(() => {
    const fieldRows: SelectDropdownOption[] = BOARD_SORT_OPTIONS.map(
      option => ({
        id: option.key,
        label: option.label,
        selected: sort.option === option.key,
        onPress: () => onSortChange(option.key),
        testID: TEST_ID_BOARD_TOOLBAR.sortOptionFor(option.key),
      })
    );

    if (!onSortDirectionChange) return fieldRows;

    const isUnsorted = sort.option === "none";
    const directionRows: SelectDropdownOption[] = BOARD_SORT_DIRECTIONS.map(
      (direction, index) => ({
        id: `direction-${direction.key}`,
        label: direction.label,
        selected: sort.direction === direction.key,
        disabled: isUnsorted,
        onPress: () => onSortDirectionChange(direction.key),
        testID: TEST_ID_BOARD_TOOLBAR.sortDirectionFor(direction.key),
        startsGroup: index === 0,
      })
    );

    return [...fieldRows, ...directionRows];
  }, [sort.option, sort.direction, onSortChange, onSortDirectionChange]);

  /**
   * The preserved-but-undesigned controls.
   *
   * Each row reproduces the visibility gate its original header button carried,
   * so relocating them cannot widen who can reach them:
   * - Stages — pipeline boards only, editors only, and requires a board id
   *   (the original button returned null without one).
   * - Share — the viewer's share permission plus a handler.
   * - Tags — always available where the header rendered it.
   */
  const overflowItems = useMemo<DesktopDropdownMenuItem[]>(() => {
    const canEditStages =
      isRosterBoard === false && (isOwner || canEdit) && Boolean(boardId);

    const candidates: readonly (DesktopDropdownMenuItem | null)[] = [
      canEditStages && boardId
        ? {
            id: "stages",
            label: "Stages",
            /* SE-6924 — same 16px-menu constraint as the column menu's
               reorder row: bar/columns glyphs read as broken at this frame
               (Columns3 rasterises grey; Kanban reads as missing strokes).
               ArrowLeftRight states the horizontal reorder and matches the
               column menu's row for the same action. The standalone Stages
               BUTTON keeps Columns3 — it renders larger and is fine. */
            icon: ArrowLeftRight,
            testID: TEST_ID_BOARD_TOOLBAR.OVERFLOW_STAGES,
            onPress: () => stageEditor.open(boardId),
          }
        : null,
      canShare && onSharePress
        ? {
            id: "share",
            label: "Share",
            icon: Share2,
            testID: TEST_ID_BOARD_TOOLBAR.OVERFLOW_SHARE,
            onPress: onSharePress,
          }
        : null,
      onManageTags
        ? {
            id: "tags",
            label: "Tags",
            icon: Hash,
            testID: TEST_ID_BOARD_TOOLBAR.OVERFLOW_TAGS,
            onPress: onManageTags,
          }
        : null,
    ];

    return candidates.filter(
      (item): item is DesktopDropdownMenuItem => item !== null
    );
  }, [
    isRosterBoard,
    isOwner,
    canEdit,
    boardId,
    stageEditor,
    canShare,
    onSharePress,
    onManageTags,
  ]);

  return {
    sortLabel: boardSortButtonLabel(sort),
    isSortActive: sort.option !== "none",
    sortDirection: sort.direction,
    sortOptions,
    isSortOpen,
    onSortToggle,
    onSortClose,
    overflowItems,
    isOverflowOpen,
    onOverflowToggle,
    onOverflowClose,
    /* See UseBoardToolbarResult.canSeeActivity — read surface, open to every
       viewer per Raniel's 2026-08-06 call. */
    canSeeActivity: true,
  };
}
